/**
 * Sales tax charged, by rule and by period.
 *
 * WHY THIS ONE IS TREATED DIFFERENTLY FROM THE OTHER REPORTS
 * ----------------------------------------------------------
 * It is the only report whose figures leave the building. Everything else here
 * informs a decision somebody inside can revisit; these numbers get copied
 * onto a return, and a return is a statement to an authority.
 *
 * So the page leads with what is wrong with the figures rather than burying it
 * under them. Two things are, today:
 *
 * Nothing has been filed. Every tax transaction in the system sits at
 * "pending", because transmitting to the FBR is Phase 6 and does not exist
 * yet. A page of tax figures that does not say so invites the reader to
 * assume the opposite.
 *
 * And the figures do not reconcile. On every discounted order, the tax was
 * worked out on the amount before the discount while the taxable value
 * recorded is the amount after it — so the two contradict each other and one
 * of them is wrong. Which one is a question for whoever signs the return; the
 * report's job is to make sure they are asked it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatPKR, today, dateKey } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { ReportExport } from './ReportExport.jsx';
import { ReportTabs } from './ReportTabs.jsx';

const startOfMonth = (day) => `${day.slice(0, 7)}-01`;

export default function TaxReportPage() {
  const [from, setFrom] = useState(() => startOfMonth(today()));
  const [to, setTo] = useState(today);

  const query = { start_date: from, end_date: to };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['reports', 'tax', from, to],
    queryFn: () => api.get(withQuery('/reports/tax', query)),
  });

  function setMonthsAgo(months) {
    const start = new Date();
    start.setMonth(start.getMonth() - months, 1);
    const end = months === 0 ? new Date() : new Date(start.getFullYear(), start.getMonth() + 1, 0);
    setFrom(dateKey(start));
    setTo(dateKey(end));
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const mismatch = data?.consistency?.disagreeing ?? 0;

  return (
    <div className="mx-auto max-w-4xl p-4">
      <ReportTabs />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Tax report</h1>
          <p className="text-sm text-ink-3">Sales tax charged on orders, by rule</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ReportExport
            path={withQuery('/reports/tax', query)}
            name={`Tax report ${from} to ${to}`}
          />
          {/* The rate these figures were charged at is set here, and somebody
              reading a tax report is the person most likely to want it. */}
          <Link to="/settings/tax" className="text-sm text-ink-3 underline hover:text-ink">
            Tax rate
          </Link>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-ink-3">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink"
          />
        </label>
        <button
          type="button"
          onClick={() => setMonthsAgo(0)}
          className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink"
        >
          This month
        </button>
        <button
          type="button"
          onClick={() => setMonthsAgo(1)}
          className="h-10 rounded-lg border border-rule px-3 text-sm text-ink-2 hover:text-ink"
        >
          Last month
        </button>
      </div>

      {isLoading && <Spinner label="Working out the tax" />}

      {isError && (
        <p role="alert" className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error?.message ?? 'That report could not be produced.'}
        </p>
      )}

      {data && (
        <>
          {/*
            Above the figures, not below them. A warning under a table is read
            after somebody has already decided what the numbers mean.
          */}
          {mismatch > 0 && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-bad/40 bg-bad-bg px-3.5 py-3 text-sm text-bad"
            >
              <p className="font-medium">These figures do not add up. Do not file them yet.</p>
              <p className="mt-1.5 text-ink-2">
                On {mismatch} order{mismatch === 1 ? '' : 's'} the tax charged does not match the
                rate applied to the taxable value recorded. The tax was worked out on the amount{' '}
                <b>before</b> the discount, while the taxable value shown is the amount{' '}
                <b>after</b> it. Charged {formatPKR(data.consistency.charged)}; the recorded values
                imply {formatPKR(data.consistency.implied)} — a difference of{' '}
                {formatPKR(Math.abs(data.consistency.gap))}. Somebody has to decide which of the two
                is right.
              </p>
            </div>
          )}

          <div className="mb-4 rounded-lg bg-surface-3 px-3.5 py-2.5 text-sm text-ink-2">
            {/*
              Said plainly. Somebody reading a page of tax figures will assume
              they have been submitted unless told otherwise.
            */}
            Nothing here has been sent to any authority. Filing is not built yet, so every one of
            these is recorded as still owed.
          </div>

          <div className="overflow-x-auto rounded-xl border border-rule">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-rule text-left text-xs text-ink-3">
                  <th className="px-3 py-2 font-medium">Period</th>
                  <th className="px-3 py-2 font-medium">Tax rule</th>
                  <th className="px-3 py-2 font-medium">Rate</th>
                  <th className="px-3 py-2 text-right font-medium">Orders</th>
                  <th className="px-3 py-2 text-right font-medium">Taxable amount</th>
                  <th className="px-3 py-2 text-right font-medium">Tax charged</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-ink-3">
                      No tax was charged in this period.
                    </td>
                  </tr>
                )}
                {rows.map((row, index) => (
                  <tr key={index} className="border-b border-rule/50">
                    <td className="px-3 py-2 text-ink-2">{row.period}</td>
                    <td className="px-3 py-2 text-ink">{row.rule}</td>
                    <td className="px-3 py-2 text-ink-2">{row.rate}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">{row.orders}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink-2">
                      {formatPKR(row.taxable)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {formatPKR(row.tax)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr className="font-medium">
                    <td className="px-3 py-2.5 text-ink" colSpan={3}>
                      Total
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                      {totals.orders}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                      {formatPKR(totals.taxable)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                      {formatPKR(totals.tax)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {Number(data.refunds?.refunds ?? 0) > 0 && (
            <p className="mt-3 text-sm text-ink-3">
              {/*
                Named rather than netted off. A refund records an amount and
                nothing about which tax it reversed, so splitting one would
                mean inventing the split.
              */}
              {data.refunds.refunds} refund{data.refunds.refunds === 1 ? '' : 's'} totalling{' '}
              {formatPKR(data.refunds.amount)} were made in this period. They are not taken off the
              figures above, because a refund does not record which tax it reversed.
            </p>
          )}

          <p className="mt-1.5 text-sm text-ink-3">Voided and cancelled orders are excluded.</p>
        </>
      )}
    </div>
  );
}
